import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { Alerta, Botao } from './base';
import { Caixa, Campo } from './campos';
import { Modal } from './Modal';

/** Dados que a tela de clínicas já tem em mãos e passa para a alteração. */
export interface ClinicaParaEditar {
  readonly id: string;
  readonly nome: string;
  readonly cnpj?: string | null;
  readonly responsavel: string | null;
  readonly telefone?: string | null;
  readonly email?: string | null;
  readonly chavePix?: string | null;
  readonly percentualHonorarios: number;
  /** Em centavos, como toda saída de dinheiro da API. */
  readonly mensalidade: number;
  readonly recebidoAnterior?: number;
  readonly honorariosAnterior?: number;
  readonly diaRepasse: number;
  readonly adiantaCustas: boolean;
  readonly contrato?: { assinadoEm: string | null; meses: number | null };
  readonly fasesDeAcompanhamento?: boolean;
}

/** Tudo texto no formulário; a API interpreta números e normaliza documento/telefone. */
const EM_BRANCO = {
  nome: '',
  cnpj: '',
  responsavel: '',
  telefone: '',
  email: '',
  chavePix: '',
  percentualHonorarios: '',
  mensalidade: '',
  diaRepasse: '30',
  recebidoAnterior: '',
  honorariosAnterior: '',
  contratoAssinadoEm: '',
  contratoMeses: '',
};

type Formulario = typeof EM_BRANCO;

/** Centavos → "1.621,00". Sem o "R$": dentro do campo o símbolo já está no rótulo. */
function reais(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Cadastro e alteração de clínica contratante.
 *
 * É o MESMO formulário nos dois casos, e de propósito: as condições comerciais
 * mudam — renegocia-se o percentual, a mensalidade entra depois do contrato
 * assinado, o PIX troca de conta. Manter duas telas parecidas para criar e alterar
 * garante que um campo novo entre numa e seja esquecido na outra.
 *
 * O que muda é o ENVIO. Ao criar, vai tudo o que foi preenchido. Ao alterar, vão só
 * os campos que a pessoa realmente mexeu: a rota é `PATCH`, e reenviar o formulário
 * inteiro faria uma correção de telefone regravar percentual e mensalidade por
 * cima — inclusive por cima de uma alteração que outra pessoa tenha feito enquanto
 * este diálogo estava aberto.
 */
export function ModalDeClinica({
  aberto,
  aoFechar,
  clinica,
}: {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente, o diálogo cadastra. Presente, altera esta clínica. */
  clinica?: ClinicaParaEditar | undefined;
}) {
  /* O id é gerado POR INSTÂNCIA, e não é constante de módulo.
     A tela de clínicas monta dois destes diálogos ao mesmo tempo — cadastro e
     edição —, e com id fixo os dois `<form>` ficavam com o MESMO id no documento.
     O botão do rodapé vive fora do `<form>` e se liga a ele pelo atributo `form=`,
     que resolve pelo PRIMEIRO id igual: "Salvar alterações" submetia o formulário
     de cadastro, vazio e fechado, e a edição não gravava nada. */
  const ID_DO_FORMULARIO = useId();

  const clienteConsulta = useQueryClient();
  const editando = clinica !== undefined;

  const [dados, setDados] = useState<Formulario>(EM_BRANCO);
  const [original, setOriginal] = useState<Formulario>(EM_BRANCO);
  const [adiantaCustas, setAdiantaCustas] = useState(false);
  const [adiantaOriginal, setAdiantaOriginal] = useState(false);
  const [fasesDeAcompanhamento, setAcompanhaSituacao] = useState(false);
  const [fasesOriginal, setAcompanhaOriginal] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [invalidos, setInvalidos] = useState<Record<string, string[]>>({});

  /* O formulário é semeado por efeito porque a clínica escolhida chega depois da
     primeira renderização — e trocar de clínica sem resemear deixaria os dados de
     uma dentro do diálogo da outra. */
  useEffect(() => {
    const inicial: Formulario = clinica
      ? {
          nome: clinica.nome,
          cnpj: clinica.cnpj ?? '',
          responsavel: clinica.responsavel ?? '',
          telefone: clinica.telefone ?? '',
          email: clinica.email ?? '',
          chavePix: clinica.chavePix ?? '',
          percentualHonorarios: String(clinica.percentualHonorarios).replace('.', ','),
          mensalidade: reais(clinica.mensalidade),
          diaRepasse: String(clinica.diaRepasse),
          contratoAssinadoEm: clinica.contrato?.assinadoEm ?? '',
          contratoMeses:
            clinica.contrato?.meses != null ? String(clinica.contrato.meses) : '',
          recebidoAnterior: reais(clinica.recebidoAnterior ?? 0),
          honorariosAnterior: reais(clinica.honorariosAnterior ?? 0),
        }
      : EM_BRANCO;

    setDados(inicial);
    setOriginal(inicial);
    setAdiantaCustas(clinica?.adiantaCustas ?? false);
    setAdiantaOriginal(clinica?.adiantaCustas ?? false);
    setAcompanhaSituacao(clinica?.fasesDeAcompanhamento ?? false);
    setAcompanhaOriginal(clinica?.fasesDeAcompanhamento ?? false);
    setErro(null);
    setInvalidos({});
  }, [clinica, aberto]);

  function definir(campo: keyof Formulario, valor: string) {
    setDados((atual) => ({ ...atual, [campo]: valor }));
    setInvalidos((atual) => {
      if (!atual[campo]) return atual;
      const { [campo]: _corrigido, ...resto } = atual;
      return resto;
    });
  }

  function fechar() {
    setErro(null);
    setInvalidos({});
    aoFechar();
  }

  const salvar = useMutation({
    mutationFn: () => {
      // "22,5" é como se escreve percentual em português; `Number` não lê vírgula.
      const numerico = (v: string) => v.replace(',', '.');

      if (!editando) {
        // Campo vazio é campo não informado. Booleanos não entram nesse filtro.
        const corpo = {
          ...Object.fromEntries(
            Object.entries(dados)
              .filter(([, valor]) => valor.trim() !== '')
              .map(([chave, valor]) =>
                chave === 'percentualHonorarios' || chave === 'diaRepasse'
                  ? [chave, numerico(valor)]
                  : [chave, valor],
              ),
          ),
          adiantaCustas,
          fasesDeAcompanhamento,
        };
        return api.criar<{ id: string; nome: string }>('/clinicas', corpo);
      }

      const alterado: Record<string, string | boolean> = Object.fromEntries(
        Object.entries(dados)
          .filter(([chave, valor]) => valor !== original[chave as keyof Formulario])
          .map(([chave, valor]) =>
            chave === 'percentualHonorarios' || chave === 'diaRepasse'
              ? [chave, numerico(valor)]
              : [chave, valor],
          ),
      );
      if (adiantaCustas !== adiantaOriginal) alterado['adiantaCustas'] = adiantaCustas;
      if (fasesDeAcompanhamento !== fasesOriginal) {
        alterado['fasesDeAcompanhamento'] = fasesDeAcompanhamento;
      }

      return api.alterar<{ id: string }>(`/clinicas/${clinica.id}`, alterado);
    },
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['clinicas'] });
      fechar();
    },
    onError: (e) => {
      if (e instanceof FalhaDaApi) {
        setErro(e.message);
        setInvalidos(e.camposInvalidos ?? {});
        return;
      }
      setErro(
        editando
          ? 'Não foi possível salvar as alterações.'
          : 'Não foi possível cadastrar a clínica.',
      );
    },
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const faltando: Record<string, string[]> = {};
    if (dados.nome.trim().length < 2) faltando['nome'] = ['Informe o nome da clínica.'];
    if (dados.percentualHonorarios.trim() === '') {
      faltando['percentualHonorarios'] = ['Informe o percentual de honorários.'];
    }

    setInvalidos(faltando);
    if (Object.keys(faltando).length > 0) {
      setErro(null);
      return;
    }
    salvar.mutate();
  }

  const erroDe = (campo: string) => {
    const mensagem = invalidos[campo]?.[0];
    return mensagem ? { erro: mensagem } : {};
  };

  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo={editando ? `Editar ${clinica.nome}` : 'Nova clínica'}
      descricao={
        editando
          ? 'Dados de contato e condições comerciais do contrato.'
          : 'Contratante cujos débitos entram na carteira do escritório.'
      }
      largura="42rem"
      rodape={
        <>
          <Botao onClick={fechar} desabilitado={salvar.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            submeter
            formulario={ID_DO_FORMULARIO}
            desabilitado={salvar.isPending}
          >
            {salvar.isPending
              ? 'Salvando…'
              : editando
                ? 'Salvar alterações'
                : 'Cadastrar clínica'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} onSubmit={submeter} className="space-y-5">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        <Campo
          rotulo="Nome da clínica"
          valor={dados.nome}
          aoMudar={(v) => definir('nome', v)}
          placeholder="Clínica Odontológica Exemplo"
          {...erroDe('nome')}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="CNPJ"
            valor={dados.cnpj}
            aoMudar={(v) => definir('cnpj', v)}
            placeholder="00.000.000/0001-00"
            {...erroDe('cnpj')}
          />
          <Campo
            rotulo="Responsável"
            valor={dados.responsavel}
            aoMudar={(v) => definir('responsavel', v)}
            placeholder="Nome de quem fala pela clínica"
            {...erroDe('responsavel')}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Telefone"
            valor={dados.telefone}
            aoMudar={(v) => definir('telefone', v)}
            placeholder="(14) 98142-7809"
            {...erroDe('telefone')}
          />
          <Campo
            rotulo="E-mail"
            tipo="email"
            valor={dados.email}
            aoMudar={(v) => definir('email', v)}
            placeholder="opcional"
            {...erroDe('email')}
          />
        </div>

        <Campo
          rotulo="Chave PIX"
          valor={dados.chavePix}
          aoMudar={(v) => definir('chavePix', v)}
          placeholder="Onde o devedor paga — conta da clínica"
          auxilio="O dinheiro entra na conta dela; o escritório só registra a baixa."
          {...erroDe('chavePix')}
        />

        <fieldset className="space-y-4 border-t pt-5">
          <legend className="sr-only">Condições comerciais</legend>
          <p className="text-xs text-[color:var(--texto-tenue)]">
            Condições do contrato — o que a clínica paga ao escritório no dia do
            repasse.
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Campo
              rotulo="Percentual de êxito (%)"
              valor={dados.percentualHonorarios}
              aoMudar={(v) => definir('percentualHonorarios', v)}
              placeholder="22"
              auxilio="Sobre o recuperado."
              {...erroDe('percentualHonorarios')}
            />
            <Campo
              rotulo="Mensalidade (R$)"
              valor={dados.mensalidade}
              aoMudar={(v) => definir('mensalidade', v)}
              placeholder="0,00"
              auxilio="Devida mesmo sem recuperação."
              {...erroDe('mensalidade')}
            />
            <Campo
              rotulo="Dia do repasse"
              valor={dados.diaRepasse}
              aoMudar={(v) => definir('diaRepasse', v)}
              placeholder="30"
              auxilio="Dia do mês, de 1 a 31."
              {...erroDe('diaRepasse')}
            />
          </div>

          {/* A DATA FINAL NÃO SE DIGITA: ela sai destes dois. Ter os três campos
              deixaria os três divergirem, e a tela passaria a avisar o vencimento
              errado — pior do que não avisar nada. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Contrato assinado em"
              tipo="date"
              valor={dados.contratoAssinadoEm}
              aoMudar={(v) => definir('contratoAssinadoEm', v)}
              auxilio="A data no instrumento."
              {...erroDe('contratoAssinadoEm')}
            />
            <Campo
              rotulo="Contrato (meses)"
              valor={dados.contratoMeses}
              aoMudar={(v) => definir('contratoMeses', v)}
              placeholder="12"
              auxilio="Vazio ou 0 = sem prazo determinado."
              {...erroDe('contratoMeses')}
            />
          </div>

          <Caixa
            rotulo="O escritório adianta as custas e é reembolsado no fechamento"
            marcado={adiantaCustas}
            aoMudar={setAdiantaCustas}
          />

          <div>
            <Caixa
              rotulo="Usar as fases de acompanhamento nesta carteira"
              marcado={fasesDeAcompanhamento}
              aoMudar={setAcompanhaSituacao}
            />
            {/* Ligada por carteira porque só compensa onde a fase parou de
                distinguir: com quase todo mundo em "acordo extrajudicial" por
                meses, o funil vira uma barra só. Numa carteira pequena, em que
                cada caso anda pelo funil, seriam cinco opções a mais sem uso. */}
            <p className="mt-1.5 text-xs text-[color:var(--texto-tenue)]">
              Acrescenta <strong>a vencer</strong>, <strong>em dia</strong>,{' '}
              <strong>em atraso</strong>, <strong>acordo em atraso</strong> e{' '}
              <strong>acordo judicial</strong> ao “Mover para a fase” dos clientes
              desta clínica. São movidas por você, como as demais.
            </p>
          </div>
        </fieldset>

        <fieldset className="space-y-4 border-t pt-5">
          <legend className="sr-only">Herança das planilhas</legend>
          <p className="text-xs text-[color:var(--texto-tenue)]">
            O que esta clínica já havia recuperado <strong>antes do CobraJus</strong>. O
            sistema só conhece o que foi baixado aqui — sem estes números, o painel diz
            que a carteira rendeu menos do que rendeu. Não viram pagamento e não entram
            no fechamento; são ponto de partida.
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Já recebido antes (R$)"
              valor={dados.recebidoAnterior}
              aoMudar={(v) => definir('recebidoAnterior', v)}
              placeholder="0,00"
              auxilio="Total recuperado pelas planilhas."
              {...erroDe('recebidoAnterior')}
            />
            <Campo
              rotulo="Honorários daquilo (R$)"
              valor={dados.honorariosAnterior}
              aoMudar={(v) => definir('honorariosAnterior', v)}
              placeholder="0,00"
              auxilio="Quanto disso foi do escritório."
              {...erroDe('honorariosAnterior')}
            />
          </div>
        </fieldset>

        {/* A regra vale para pagamento JÁ apurado: o percentual é congelado na
            baixa. Sem este aviso, alterar o percentual parece recalcular o passado. */}
        {editando && (
          <p className="border-t pt-4 text-xs text-[color:var(--texto-tenue)]">
            Mudar o percentual de êxito vale para as baixas futuras. Pagamento já
            apurado mantém o percentual que valia no dia — o fechamento fechado não
            se altera sozinho.
          </p>
        )}
      </form>
    </Modal>
  );
}
